import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ApplicationForm from "@/components/ApplicationForm";
import useSeo from "@/hooks/useSeo";
import { JOBS, EMPLOYER_INTRO, BENEFITS } from "@/data/jobs";
import { ArrowLeft, ArrowDown, Check } from "lucide-react";

const SectionTitle = ({ children }) => (
  <h2 className="font-heading font-black uppercase tracking-tight text-white text-2xl md:text-3xl mb-6">
    {children}
  </h2>
);

export default function KarriereJob() {
  const { slug } = useParams();
  const job = JOBS.find((j) => j.slug === slug);

  useEffect(() => window.scrollTo(0, 0), [slug]);

  useSeo(job ? {
    title: `${job.title} in Köln · Jetzt bewerben | DNA Automotive`,
    description: `${job.title} – ${job.employmentType} | ${job.department} | Köln. ${job.teaser} Jetzt bei DNA Automotive GmbH bewerben.`,
    path: `/karriere/${job.slug}`,
    image: `/og/job-${job.slug}.png`,
    breadcrumbs: [
      { name: "Karriere", url: "/karriere" },
      { name: job.title, url: `/karriere/${job.slug}` },
    ],
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: job.title,
      description: `${job.teaser} Aufgaben: ${job.tasks.join(". ")}. Profil: ${job.profile.join(". ")}.`,
      hiringOrganization: {
        "@type": "Organization",
        name: "DNA Automotive GmbH",
        sameAs: "https://dna-automotive.de",
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Butzweilerstraße 35-39",
          addressLocality: "Köln",
          postalCode: "50829",
          addressCountry: "DE",
        },
      },
      employmentType: job.employmentType === "Vollzeit" ? "FULL_TIME" : "PART_TIME",
    },
  } : { title: "Stelle nicht gefunden | DNA Automotive", path: "/karriere" });

  if (!job) {
    return (
      <div className="bg-obsidian min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-40 pb-24 text-center">
          <h1 className="font-heading font-black uppercase text-white text-3xl mb-6">Stelle nicht gefunden</h1>
          <Link to="/karriere" className="text-brand-bright hover:underline uppercase tracking-wider text-sm">Zur Karriere-Übersicht</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const scrollToForm = () => document.getElementById("bewerben")?.scrollIntoView({ behavior: "smooth" });

  const overview = [
    ["Anstellungsart", job.employmentType],
    ["Abteilung", job.department],
    ["Standort", job.location],
    ["Arbeitsbeginn", "Nach Vereinbarung"],
    ["Unternehmen", "DNA Automotive GmbH"],
  ];

  return (
    <div className="bg-obsidian min-h-screen" data-testid="job-detail-page">
      <Header />

      {/* Kopfbereich */}
      <section className="pt-36 pb-14 px-6 md:px-10 grain">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/karriere" className="inline-flex items-center gap-2 text-zinc-400 hover:text-brand-bright text-xs uppercase tracking-widest mb-10 transition-colors" data-testid="job-back-link">
              <ArrowLeft className="w-4 h-4" />
              Alle Stellen
            </Link>
            <h1 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl leading-[0.95] mb-8" data-testid="job-title">
              {job.title}
            </h1>

            {/* Kompakte Übersicht */}
            <div className="bg-obsidian-surface border border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-px" data-testid="job-overview">
              {overview.map(([k, v]) => (
                <div key={k} className="bg-obsidian-surface px-5 py-4 flex items-baseline gap-2">
                  <span className="text-zinc-500 text-xs uppercase tracking-wider shrink-0">{k}:</span>
                  <span className="text-white text-sm font-medium">{v}</span>
                </div>
              ))}
            </div>

            <p className="text-zinc-300 leading-relaxed text-base md:text-lg mt-8">{job.teaser}</p>

            <button
              onClick={scrollToForm}
              className="group mt-8 inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-10 py-5 hover:bg-brand-hover transition-colors"
              data-testid="job-apply-top"
            >
              Jetzt bewerben
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Inhalt */}
      <section className="px-6 md:px-10 pb-16">
        <div className="max-w-4xl mx-auto space-y-16">
          {/* Über uns */}
          <div data-testid="job-about">
            <SectionTitle>Über uns</SectionTitle>
            <p className="text-zinc-300 leading-relaxed">{EMPLOYER_INTRO}</p>
          </div>

          {/* Deine Aufgaben */}
          <div data-testid="job-tasks">
            <SectionTitle>Deine Aufgaben</SectionTitle>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {job.tasks.map((t) => (
                <li key={t} className="flex items-start gap-3 text-zinc-300 leading-relaxed">
                  <Check className="w-5 h-5 text-brand-bright shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Dein Profil */}
          <div data-testid="job-profile">
            <SectionTitle>Dein Profil</SectionTitle>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {job.profile.map((p) => (
                <li key={p} className="flex items-start gap-3 text-zinc-300 leading-relaxed">
                  <Check className="w-5 h-5 text-brand-bright shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Das bieten wir dir */}
          <div data-testid="job-benefits">
            <SectionTitle>Das bieten wir dir</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {BENEFITS.map((b, i) => (
                <div key={b} className="bg-obsidian px-5 py-5 flex items-start gap-3" data-testid={`benefit-${i}`}>
                  <Check className="w-4 h-4 text-brand-bright shrink-0 mt-1" />
                  <span className="text-zinc-300 text-sm leading-relaxed">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bewerbung */}
      <section id="bewerben" className="px-6 md:px-10 pb-24 scroll-mt-28" data-testid="job-apply-section">
        <div className="max-w-4xl mx-auto">
          <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">Bewerbung</p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-5xl mb-8">
            Jetzt bewerben
          </h2>
          <ApplicationForm position={job.title} />
        </div>
      </section>

      <Footer />
      <WhatsAppButton prefill={`Hallo DNA Automotive, ich interessiere mich für die Stelle: ${job.title}.`} />
    </div>
  );
}
